import { capitalize } from "@/lib/util";
import fetchJson from "@/lib/fetchJson";
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  IconButton,
  Text,
  TextArea,
} from "@radix-ui/themes";
import { IconCheck, IconTrash, IconX } from "@tabler/icons-react";
import * as Dialog from "@/components/CustomUiComponents/Dialog";
import { useMemo, useState } from "react";

interface PostDraftsProps {
  drafts: PostDraft[];
  mutate: () => void;
  editable: boolean;
}

const STATUS_COLOR: Record<string, "gray" | "green" | "red" | "yellow"> = {
  draft: "yellow",
  approved: "green",
  rejected: "red",
};

const DraftsTab = ({ drafts, mutate, editable }: PostDraftsProps) => {
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [feedback, setFeedback] = useState("");
  const [loading, setLoading] = useState(false);

  const sorted = useMemo(() => {
    return [...(drafts || [])].sort((a, b) => {
      const ta = new Date((a as any).createdAt).getTime();
      const tb = new Date((b as any).createdAt).getTime();
      return tb - ta;
    });
  }, [drafts]);

  const remove = async (draftId: number) => {
    if (confirm("Are you sure you want to delete this draft?")) {
      await fetchJson(`/postDraft/${draftId}`, { method: "DELETE" });
      mutate();
    }
  };

  const review = async (draftId: number, status: string, feedbackText?: string) => {
    setLoading(true);
    try {
      await fetchJson(`/postDraft/${draftId}`, {
        method: "POST",
        body: JSON.stringify({
          status,
          ...(feedbackText && { feedback: feedbackText }),
        }),
      });
      setRejectingId(null);
      setFeedback("");
      mutate();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (!sorted.length) {
    return (
      <Text size="2" color="gray">
        No drafts yet. Creators submit drafts from their campaign offer link.
      </Text>
    );
  }

  return (
    <Box
      style={{
        columnWidth: 280,
        columnGap: "var(--space-4)",
      }}
    >
      {sorted.map((draft) => {
        const cp = (draft as any).creatorPrice;
        const username = cp?.username;
        const fb = (draft as any).feedback as string | undefined;

        return (
          <Box
            key={draft.id}
            style={{
              breakInside: "avoid",
              marginBottom: "var(--space-4)",
            }}
          >
            <Card size="2">
              <Flex direction="column" gap="3">
                <Box
                  style={{
                    borderRadius: "var(--radius-3)",
                    overflow: "hidden",
                    background: "var(--gray-a3)",
                  }}
                >
                  {draft.videoPath ? (
                    <video
                      src={`${process.env.NEXT_PUBLIC_DOMAIN}/image-upload/${draft.videoPath}`}
                      controls
                      playsInline
                      style={{
                        display: "block",
                        width: "100%",
                        maxHeight: 420,
                        objectFit: "contain",
                      }}
                    />
                  ) : draft.thumbnail ? (
                    <img
                      src={`/image-upload/${draft.thumbnail}`}
                      alt=""
                      style={{
                        display: "block",
                        width: "100%",
                        maxHeight: 420,
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <Box p="4">
                      <Text size="2" color="gray">
                        No preview
                      </Text>
                    </Box>
                  )}
                </Box>

                <Flex direction="column" gap="2">
                  {username && (
                    <Text size="2" weight="medium">
                      @{username}
                    </Text>
                  )}
                  <Flex align="center" gap="2" wrap="wrap">
                    <Badge
                      color={STATUS_COLOR[draft.status] || "gray"}
                      variant="soft"
                      size="1"
                    >
                      {capitalize(draft.status)}
                    </Badge>
                    {(draft as any).createdAt && (
                      <Text size="1" color="gray">
                        {new Date((draft as any).createdAt).toLocaleString()}
                      </Text>
                    )}
                  </Flex>
                  {fb && (
                    <Text size="1" color="gray" style={{ whiteSpace: "pre-wrap" }}>
                      {fb}
                    </Text>
                  )}
                </Flex>

                {editable && (
                  <Flex direction="column" gap="2">
                    {draft.status === "draft" && (
                      <>
                        {rejectingId === draft.id ? (
                          <Flex direction="column" gap="2">
                            <TextArea
                              size="2"
                              placeholder="Feedback for creator (required)"
                              rows={3}
                              value={feedback}
                              onChange={(e) => setFeedback(e.target.value)}
                              variant="soft"
                            />
                            <Flex gap="2" wrap="wrap">
                              <Button
                                size="2"
                                color="red"
                                disabled={loading || !feedback.trim()}
                                onClick={() => review(draft.id, "rejected", feedback)}
                              >
                                Confirm reject
                              </Button>
                              <Button
                                size="2"
                                variant="soft"
                                color="gray"
                                disabled={loading}
                                onClick={() => {
                                  setRejectingId(null);
                                  setFeedback("");
                                }}
                              >
                                Cancel
                              </Button>
                            </Flex>
                          </Flex>
                        ) : (
                          <Flex gap="2" wrap="wrap">
                            <Button
                              size="2"
                              variant="soft"
                              color="green"
                              disabled={loading}
                              onClick={() => review(draft.id, "approved")}
                              style={{ flex: "1 1 auto", minWidth: 100 }}
                            >
                              <IconCheck size={16} />
                              Approve
                            </Button>
                            <Button
                              size="2"
                              variant="soft"
                              color="red"
                              disabled={loading}
                              onClick={() => setRejectingId(draft.id)}
                              style={{ flex: "1 1 auto", minWidth: 100 }}
                            >
                              <IconX size={16} />
                              Reject
                            </Button>
                          </Flex>
                        )}
                      </>
                    )}
                    <Flex gap="2" align="center" wrap="wrap">
                      <Dialog.Root>
                        <Dialog.Trigger>
                          <Button size="1" variant="ghost" color="gray">
                            Full screen
                          </Button>
                        </Dialog.Trigger>
                        <Dialog.Portal>
                          <Dialog.Content style={{ maxWidth: "min(96vw, 960px)" }}>
                            <Flex direction="column" gap="2">
                              <Dialog.Close>
                                <Flex justify="end">
                                  <IconButton variant="ghost" aria-label="Close">
                                    <IconX />
                                  </IconButton>
                                </Flex>
                              </Dialog.Close>
                              {draft.videoPath ? (
                                <video
                                  src={`${process.env.NEXT_PUBLIC_DOMAIN}/image-upload/${draft.videoPath}`}
                                  controls
                                  playsInline
                                  style={{ width: "100%", maxHeight: "80vh" }}
                                />
                              ) : draft.thumbnail ? (
                                <img
                                  src={`/image-upload/${draft.thumbnail}`}
                                  alt=""
                                  style={{ width: "100%", maxHeight: "80vh", objectFit: "contain" }}
                                />
                              ) : null}
                            </Flex>
                          </Dialog.Content>
                        </Dialog.Portal>
                      </Dialog.Root>
                      <IconButton
                        variant="outline"
                        color="red"
                        size="2"
                        aria-label="Delete draft"
                        onClick={() => remove(draft.id)}
                      >
                        <IconTrash size={16} />
                      </IconButton>
                    </Flex>
                  </Flex>
                )}
              </Flex>
            </Card>
          </Box>
        );
      })}
    </Box>
  );
};

export default DraftsTab;
